import { z } from "zod";
import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";

export const ACTION_PLAN_TEMPLATES_PAGE_SIZE = 20;

export const ActionPlanTemplateSchema = z.object({
    id: z.string(),
    name: z.string(),
    version: z.number().int().positive(),
    status: z.enum(["draft", "published", "archived"]),
    sectorId: z.number().int(),
    sectorName: z.string(),
    ssqId: z.number(),
    ssqVersionId: z.number().int().positive(),
    ssqTitle: z.string().nullable(),
    actionCount: z.number().int().nonnegative(),
    canView: z.boolean(),
    canModify: z.boolean(),
    canDelete: z.boolean(),
});

const CollectionSchema = z.object({
    "hydra:member": ActionPlanTemplateSchema.array(),
    "hydra:totalItems": z.number().int().nonnegative(),
});

export type ActionPlanTemplate = z.infer<typeof ActionPlanTemplateSchema>;
export type TemplateSortField = "name" | "status" | "sectorName" | "ssqTitle" | "actionCount";
export type TemplateSort = { field: TemplateSortField; direction: "asc" | "desc" };

export const templateListParams = (page: number, sort: TemplateSort | null) => ({
    page,
    itemsPerPage: ACTION_PLAN_TEMPLATES_PAGE_SIZE,
    ...(sort ? { [`order[${sort.field}]`]: sort.direction } : {}),
});

export const useActionPlanTemplates = (page: number, sort: TemplateSort | null) =>
    useQuery({
        queryKey: ["actionPlanTemplates", page, sort],
        queryFn: async () => {
            const response = await loggedInApiCall.get("/api/action-plan-templates", {
                params: templateListParams(page, sort),
            });
            return zodParseWithLog(response.data, CollectionSchema, "actionPlanTemplates");
        },
    });
